import React, { useState, useMemo, useEffect } from 'react';
import { Spinner } from '@openedx/paragon';
import { useIntl } from '@edx/frontend-platform/i18n';
import { getFeatureToggle } from '@blend-ed/blendxplatform/utils';

import { useDiscoverCourses } from '../../gql/hooks/useDiscoverCourses';
import { useDiscoverPrograms } from '../../gql/hooks/useDiscoverPrograms';
import { useDiscoverPathways } from '../../gql/hooks/useDiscoverPathways';
import { useCourseTagsBatch } from '../../gql/hooks/useCourseTagsBatch';
import { useContentRatings } from '../../gql/hooks/useContentRatings';
import { useFetchBanners } from '../../gql';
import { useCoursesEffort } from './useCoursesEffort';
import { useCoursesIsNew } from './useCoursesIsNew';
import {
  DiscoverBanner,
  ExploreCoursesHeader,
  DiscoverCardsGrid,
} from './components';
import { parseDiscoverCourses, parseDiscoverPrograms, parseDiscoverPathways } from './courseDataParser';
import messages from './components/messages';

import './index.scss';

const Discover = () => {
  const intl = useIntl();
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('newest');
  const featuresToggled = getFeatureToggle();
  const paymentsEnabled = !featuresToggled.loading && featuresToggled.features_payments === true;
  const { banners } = useFetchBanners();

  useEffect(() => {
    if (!paymentsEnabled && (sortValue === 'priceAsc' || sortValue === 'priceDesc')) {
      setSortValue('newest');
    }
  }, [paymentsEnabled, sortValue]);

  const {
    discoverCourses,
    loading: coursesLoading,
    error: coursesError,
  } = useDiscoverCourses();
  const programsEnabled = featuresToggled.features_programs !== false;
  const {
    discoverPrograms,
    loading: programsLoading,
    error: programsError,
  } = useDiscoverPrograms(programsEnabled);
  const pathwaysEnabled = featuresToggled.features_learning_pathway === true;
  const {
    discoverPathways,
    loading: pathwaysLoading,
  } = useDiscoverPathways(pathwaysEnabled);

  const courseIds = useMemo(
    () => (discoverCourses || [])
      .map((c) => c?.openedxCourseId || c?.id)
      .filter(Boolean),
    [discoverCourses],
  );

  const contentIds = useMemo(() => {
    const ids = [];
    (discoverCourses || []).forEach((c) => c?.id && ids.push(c.id));
    (discoverPrograms || []).forEach((p) => p?.id && ids.push(p.id));
    return ids;
  }, [discoverCourses, discoverPrograms]);

  const { tagsByCourseId } = useCourseTagsBatch(courseIds);
  const { ratingsByContentId } = useContentRatings(contentIds);
  const { effortByCourseId } = useCoursesEffort(courseIds);
  const { isNewByCourseId } = useCoursesIsNew(courseIds);

  const loading = coursesLoading || programsLoading || pathwaysLoading;
  const error = coursesError || programsError;

  const parsedItems = useMemo(() => [
    ...parseDiscoverPrograms(discoverPrograms, ratingsByContentId),
    ...parseDiscoverPathways(discoverPathways),
    ...parseDiscoverCourses(
      discoverCourses,
      tagsByCourseId,
      ratingsByContentId,
      effortByCourseId,
      isNewByCourseId,
    ),
  ], [
    discoverCourses,
    discoverPrograms,
    discoverPathways,
    tagsByCourseId,
    ratingsByContentId,
    effortByCourseId,
    isNewByCourseId,
  ]);

  const filteredCourses = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    const base = query
      ? parsedItems.filter(
        (item) => item.title.toLowerCase().includes(query)
          || item.skills.some((skill) => skill.toLowerCase().includes(query)),
      )
      : parsedItems;

    const items = [...base];
    const ts = (v) => (v ? new Date(v).getTime() : 0);

    switch (sortValue) {
      case 'nameAsc':
        items.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'nameDesc':
        items.sort((a, b) => b.title.localeCompare(a.title));
        break;
      case 'priceAsc':
        items.sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
        break;
      case 'priceDesc':
        items.sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
        break;
      case 'rating':
        items.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
        break;
      case 'newest':
      default:
        items.sort((a, b) => ts(b.createdAt) - ts(a.createdAt));
        break;
    }
    return items;
  }, [parsedItems, searchValue, sortValue]);

  return (
    <div className="discover-page">
      <DiscoverBanner banners={banners} />

      <ExploreCoursesHeader
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        sortValue={sortValue}
        onSortChange={setSortValue}
        paymentsEnabled={paymentsEnabled}
      />

      {loading && (
        <div className="discover-page__state">
          <Spinner animation="border" screenReaderText={intl.formatMessage(messages.loadingCourses)} />
        </div>
      )}

      {error && !loading && (
        <div className="discover-page__state discover-page__state--error">
          {intl.formatMessage(messages.coursesLoadError)}
        </div>
      )}

      {!loading && !error && (
        <DiscoverCardsGrid courses={filteredCourses} />
      )}
    </div>
  );
};

export default Discover;
